import { mapValues, pickBy } from 'lodash';
import { useEffect, ChangeEvent, Dispatch, SetStateAction, useCallback, FocusEvent } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as i18n from 'i18next';
import { State } from 'reducers';
import { Building, DeviceClass, DeviceTypeId, DeviceType, DeviceFamilyNames } from 'shared/models';
import { ListSelectField } from 'ui/shared/controls/listSelectField/ListSelectField';
import { getBuildingDeviceGroupNames } from 'selectors/buildingsSelectors';
import { CheckField, LegacySelectField, TextField } from 'ui/shared/controls';
import { useDeviceConfigs } from 'hooks';
import { sortNamesBy } from 'logic/sortNames';
import { DeviceConfigField } from 'ui/devices/deviceConfigField/DeviceConfigField';
import { doesDeviceTypeSupportDeviceConfig } from 'logic/doesDeviceTypeSupportDeviceConfig';
import { FormData } from "../AddDevicesForm";

require('./AddDisplayFormFields.css');

const nullValue = 'none';

const makeGroupNameOptions = (t: i18n.TFunction, groupNames: string[]) =>
    [
        // Note: couldn't use null or '' for the no-group value... Semantic UI mishandled it
        { value: nullValue, text: t('DEVICES.NO_GROUP') },
        ...groupNames.sort().map(name => ({ value: name, text: name })),
    ].map(item =>
        <option
            key={ `group-${item.text}` }
            value={ item.value }
        >
            { item.text }
        </option>
    );

export type Props = {
    building: Building;
    deviceTypes: Record<DeviceTypeId, DeviceType>;
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    data: FormData;
    setData: Dispatch<SetStateAction<FormData>>;
};

export const AddDisplayFormFields = ({ building, deviceTypes, data, setData, onChange }: Props) => {
    const { t } = useTranslation();

    const buildingId = building.id;
    const groupNames = useSelector((state: State) => buildingId ? getBuildingDeviceGroupNames(state, { buildingId }) : []);

    const { deviceConfigs, deviceConfigIds, loading: isLoadingDeviceConfigs } = useDeviceConfigs(building.id);

    //
    // Calculate which device types are in the selected device family
    //
    const displayDeviceTypes = pickBy(deviceTypes, dt => dt.deviceClass === DeviceClass.Display);
    const familyDeviceTypes = mapValues(pickBy(displayDeviceTypes, dt => (dt.deviceFamily || '') === (data.deviceFamily || '')), dt => dt.displayName);
    const compatibleDeviceConfigs =
        deviceConfigIds
            .map(id => deviceConfigs[id])
            .filter(dc => dc.properties.deviceFamily === data.deviceFamily)
            .sort(sortNamesBy(dc => dc.displayName));

    //
    // Reset the device type and device config whenever the family changes
    //
    useEffect(() => {
        setData(data => ({
            ...data,
            deviceTypeId: null,
            deviceConfigId: null,
        }));
    }, [ data.deviceFamily, setData ]);

    const supportsDeviceConfig = doesDeviceTypeSupportDeviceConfig(data);

    return (
        <div id="add-display-form-fields" className="add-devices-form-fields">
            <TextField
                id="add-devices-form_display-name"
                className="name"
                name="displayName"
                labelKey="DEVICES.EDIT_DIALOG.FIELD.DISPLAY_NAME"
                maxLength={ 32 }
                autoFocus={ true }
                autoComplete="off"
                value={ data.displayName }
                onChange={ onChange }
            />

            <CheckField
                id="add-devices-form_pi"
                className="pi"
                labelKey="LABEL.PI"
                input={ {
                    name: 'pi',
                    checked: data.pi,
                    onChange: onChange,
                } }
            />

            <ListSelectField
                id="add-devices-form_family"
                className="family"
                name="deviceFamily"
                size={ 6 }
                labelKey="DEVICES.EDIT_DIALOG.FIELD.FAMILY"
                value={ data.deviceFamily || nullValue }
                options={ DeviceFamilyNames }
                showNone={ true }
                noneValue={ nullValue }
                onChange={ onChange }
            />

            <ListSelectField
                id="add-devices-form_type"
                className="type"
                name="deviceTypeId"
                size={ 6 }
                labelKey="DEVICES.EDIT_DIALOG.FIELD.DEVICE_TYPE"
                value={ data.deviceTypeId || undefined }
                options={ familyDeviceTypes }
                required={ true }
                showNone={ false }
                onChange={ onChange }
            />

            <TextField
                id="add-devices-form_elevator-id"
                className="id"
                name="elevatorId"
                labelKey="DEVICES.EDIT_DIALOG.FIELD.ELEVATOR_ID"
                maxLength={ 32 }
                autoComplete="off"
                value={ data.elevatorId }
                onChange={ onChange }
            />

            <LegacySelectField
                id="add-devices-form_device-group"
                className="group"
                labelKey="DEVICES.EDIT_DIALOG.FIELD.GROUP"
                allowAdditions={ true }
                input={ {
                    name: 'groupName',
                    className: "ui fluid search dropdown",
                    maxLength: 32,
                    value: data.groupName || nullValue,
                    onChange: onChange,
                } }
            >
                { makeGroupNameOptions(t, groupNames) }
            </LegacySelectField>

            { supportsDeviceConfig &&
                <DeviceConfigField
                    id="add-devices-form_device-config"
                    className="config"
                    name="deviceConfigId"
                    value={ data.deviceConfigId || null }
                    deviceConfigs={ compatibleDeviceConfigs }
                    buildingId={ buildingId }
                    deviceTypeId={ data.deviceTypeId }
                    isPI={ true }
                    onChange={ onChange }
                    disabled={ !data.deviceTypeId }
                />
            }
        </div>
    );
};
